import { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { type KanbanStage } from '@/hooks/useFizbo';

type Listing = {
  id: string;
  kanbanStage: KanbanStage;
  price?: number;
  sellabilityScore?: number;
  status: string;
};

type Props = {
  listings: Listing[];
};

const MONTHLY_GOALS = {
  kapanilanIlan: 5,
  teklifAsamasi: 8,
  toplamCiro: 20000000,
  kabulOrani: 70,
};

export default function HedefTakibi({ listings }: Props) {
  const { isDark } = useTheme();
  const [editMode, setEditMode] = useState(false);
  const [goals, setGoals] = useState(MONTHLY_GOALS);
  const [tempGoals, setTempGoals] = useState(MONTHLY_GOALS);

  const kapanilanCount = listings.filter((l) => l.kanbanStage === 'Kapandı').length;
  const teklifCount = listings.filter((l) => l.kanbanStage === 'Teklif' || l.kanbanStage === 'Kapandı').length;
  const toplamCiro = listings
    .filter((l) => l.kanbanStage === 'Kapandı')
    .reduce((sum, l) => sum + (l.price ?? 0), 0);
  const kabulCount = listings.filter((l) => l.status === 'Kabul').length;
  const kabulOrani = listings.length > 0 ? Math.round((kabulCount / listings.length) * 100) : 0;

  const metrics = [
    {
      label: 'Kapanan İlan',
      current: kapanilanCount,
      goal: goals.kapanilanIlan,
      icon: 'ri-checkbox-circle-line',
      color: 'text-green-500',
      bgColor: 'bg-green-500',
      lightBg: 'bg-green-50 dark:bg-green-900/20',
      format: (v: number) => `${v}`,
      unit: 'ilan',
    },
    {
      label: 'Teklif Aşaması',
      current: teklifCount,
      goal: goals.teklifAsamasi,
      icon: 'ri-file-list-3-line',
      color: 'text-amber-500',
      bgColor: 'bg-amber-500',
      lightBg: 'bg-amber-50 dark:bg-amber-900/20',
      format: (v: number) => `${v}`,
      unit: 'ilan',
    },
    {
      label: 'Toplam Ciro',
      current: toplamCiro,
      goal: goals.toplamCiro,
      icon: 'ri-money-cny-circle-line',
      color: 'text-violet-500',
      bgColor: 'bg-violet-500',
      lightBg: 'bg-violet-50 dark:bg-violet-900/20',
      format: (v: number) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M ₺` : `${v.toLocaleString('tr-TR')} ₺`,
      unit: '₺',
    },
    {
      label: 'Kabul Oranı',
      current: kabulOrani,
      goal: goals.kabulOrani,
      icon: 'ri-percent-line',
      color: 'text-sky-500',
      bgColor: 'bg-sky-500',
      lightBg: 'bg-sky-50 dark:bg-sky-900/20',
      format: (v: number) => `%${v}`,
      unit: '%',
    },
  ];

  const handleSave = () => {
    setGoals(tempGoals);
    setEditMode(false);
  };

  return (
    <div
      className={`rounded-xl border p-4 ${
        isDark ? 'bg-dark-card border-dark-border' : 'bg-white border-light-border'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500">
            <i className="ri-trophy-line text-white text-sm" />
          </div>
          <div>
            <h3 className={`text-sm font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              Aylık Hedef Takibi
            </h3>
            <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
              Nisan 2026
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            if (editMode) {
              handleSave();
            } else {
              setTempGoals(goals);
              setEditMode(true);
            }
          }}
          className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
            editMode
              ? 'bg-primary-500 text-white'
              : isDark
              ? 'bg-dark-bg text-dark-muted hover:text-dark-text border border-dark-border'
              : 'bg-light-bg text-light-muted hover:text-light-text border border-light-border'
          }`}
        >
          {editMode ? 'Kaydet' : 'Hedef Düzenle'}
        </button>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {metrics.map((m) => {
          const pct = Math.min(100, Math.round((m.current / m.goal) * 100));
          const isAchieved = pct >= 100;

          return (
            <div
              key={m.label}
              className={`rounded-xl p-3 ${m.lightBg}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className={`${m.icon} text-base ${m.color}`} />
                </div>
                {isAchieved && (
                  <span className="text-xs font-bold text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-1.5 py-0.5 rounded-full">
                    ✓ Tamam
                  </span>
                )}
              </div>

              <p className={`text-lg font-bold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                {m.format(m.current)}
              </p>

              {editMode ? (
                <div className="mt-1.5">
                  <input
                    type="number"
                    value={
                      m.label === 'Kapanan İlan' ? tempGoals.kapanilanIlan
                      : m.label === 'Teklif Aşaması' ? tempGoals.teklifAsamasi
                      : m.label === 'Toplam Ciro' ? tempGoals.toplamCiro
                      : tempGoals.kabulOrani
                    }
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      if (m.label === 'Kapanan İlan') setTempGoals((p) => ({ ...p, kapanilanIlan: val }));
                      else if (m.label === 'Teklif Aşaması') setTempGoals((p) => ({ ...p, teklifAsamasi: val }));
                      else if (m.label === 'Toplam Ciro') setTempGoals((p) => ({ ...p, toplamCiro: val }));
                      else setTempGoals((p) => ({ ...p, kabulOrani: val }));
                    }}
                    className={`w-full text-xs px-2 py-1 rounded-lg border text-sm ${
                      isDark
                        ? 'bg-dark-bg border-dark-border text-dark-text'
                        : 'bg-white border-light-border text-light-text'
                    }`}
                  />
                  <p className={`text-xs mt-0.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                    Hedef ({m.unit})
                  </p>
                </div>
              ) : (
                <>
                  <p className={`text-xs mt-0.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                    {m.label} · Hedef: {m.format(m.goal)}
                  </p>
                  <div className={`mt-2 w-full h-1.5 rounded-full ${isDark ? 'bg-dark-border' : 'bg-white/60'}`}>
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${m.bgColor}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <p className={`text-xs mt-1 font-medium ${m.color}`}>
                    %{pct} tamamlandı
                  </p>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Motivational Footer */}
      {!editMode && (
        <div className={`mt-3 pt-3 border-t flex items-center gap-2 ${isDark ? 'border-dark-border' : 'border-light-border'}`}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-lightbulb-line text-sm ${isDark ? 'text-dark-muted' : 'text-light-muted'}`} />
          </div>
          <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
            {kapanilanCount >= goals.kapanilanIlan
              ? 'Harika! Aylık kapanış hedefinize ulaştınız. Yeni hedef belirleyin!'
              : `Hedefe ${goals.kapanilanIlan - kapanilanCount} ilan daha kapanması gerekiyor. Teklif aşamasındaki ${teklifCount} ilanı takip edin.`}
          </p>
        </div>
      )}
    </div>
  );
}
